import { PermissionsProvider } from '@carboncopyco/components';
import { useGetCurrentUserQuery } from '@carboncopyco/data';
import { Loader as AppLoader, ErrorBox } from '@carboncopyco/ui';
import { useNetwork } from 'ahooks';
import { type ReactNode, useEffect } from 'react';
import { rendererLogger } from '../../app/Logger';
import rendererService from '../../services/RendererService';
import { useUser } from '../context';

const logger = rendererLogger('PermissionProviderWrapper');

export const PermissionProviderWrapper = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { activeOrganization, setActiveOrganization } = useUser();
  const { data: userData, isLoading, isFetching } = useGetCurrentUserQuery();
  const networkResult = useNetwork();

  const handleSetActiveOrganization = async () => {
    try {
      const activeOrg = await window?.dsDesktop?.user.getSelectedOrganization();
      logger.info(
        'Setting active organization on PermissionProviderWrapper mount',
        {
          activeOrgId: activeOrg?.id,
        }
      );
      setActiveOrganization(activeOrg);
    } catch (error) {
      console.error('Error getting active organization', error);
    }
  };

  useEffect(() => {
    handleSetActiveOrganization();
  }, []);

  const organizationId =
    activeOrganization?.id || userData?.user?.defaultOrganization || 0;

  const { roles, rolesToPermissions } = userData?.user || {};
  const role = organizationId && roles?.[organizationId];

  if (
    isFetching ||
    isLoading ||
    !userData?.user ||
    !organizationId ||
    !role ||
    !rolesToPermissions
  )
    return <AppLoader show />;

  if (!networkResult.online) {
    rendererService.focusApp();
  }
  return (
    <PermissionsProvider
      rolesToPermissions={rolesToPermissions}
      role={role}
    >
      {children}
      {!networkResult.online && (
        <div className="fixed inset-0 z-[2000]">
          <ErrorBox
            title="Oops! Looks like there was a problem connecting to our server."
            message="Please check your connection and try again."
          />
        </div>
      )}
    </PermissionsProvider>
  );
};
